import React, { useState, useEffect } from 'react';
import { Box, Text, Input, HStack, FlatList, Select, useTheme } from 'native-base';
import { ImageBackground } from 'react-native';
import GoBackButton from '../content/common/GoBackButton';
import { useAuthState } from '../../context/auth';
import { useEnvState } from '../../context/env';
import { useCategoriesState } from '../../context/categories';
import { searchAPI } from '../../context/search';
import ResultItem from './ResultItem';
import { isArrayValid, combinePostsAndTopics } from '../utils/helpers';
import { getImage } from '../utils/images';
import { FontAwesomeIcon } from '@fortawesome/react-native-fontawesome';
import { faMagnifyingGlass } from '@fortawesome/pro-solid-svg-icons';
import { useDebounce } from 'use-debounce';
import { SEARCH_RESULTS_MAX, SEARCH_TERM_MIN, DEBOUNCE_DELAY, DEVICE_HEIGHT } from '../Constants';
import { LANGUAGES } from '../Constants';
const SearchForumScreen = ({
  navigation,
  route
}) => {
  const theme = useTheme(),
    {
      colors
    } = theme;
  const {
    term,
    selectedCategory
  } = route.params;
  const authState = useAuthState();
  const envState = useEnvState();
  const [apiPage, setApiPage] = useState(1);
  const [apiHasMore, setApiHasMore] = useState(true);
  const categoriesState = useCategoriesState();
  const {
    forumCategories: categories,
    rootProductCategory
  } = categoriesState;
  const [category, setCategory] = useState(selectedCategory ?? '');
  const [errorMessage, setErrorMessage] = useState('');
  const [searchTerm, setSearchTerm] = useState(term ?? '');
  const [searchResults, setSearchResults] = useState([]);
  const [searchResultsLength, setSearchResultsLength] = useState(0);
  const [debouncedValue] = useDebounce(searchTerm, DEBOUNCE_DELAY);
  const isInputValid = debouncedValue.length >= SEARCH_TERM_MIN;
  useEffect(() => {
    if (isInputValid) {
      doSearchInResultScreen();
    } else {
      handleErrorMessage();
    }
  }, [isInputValid, debouncedValue, category]);
  useEffect(() => {
    resetSearchResults();
  }, [debouncedValue, category]);
  const resetSearchResults = () => {
    setApiPage(1);
    setSearchResults([]);
    setSearchResultsLength(0);
    // setCategory('');

    return true;
  };
  const carrotsImage = {
    uri: getImage('carrots')
  };
  const renderItem = ({
    item: resultItem
  }) => /*#__PURE__*/React.createElement(ResultItem, {
    key: resultItem.id.toString() + resultItem.created_at,
    item: resultItem,
    navigation: navigation,
    term: searchTerm ? searchTerm : term
  });
  const handleErrorMessage = () => {
    if (searchTerm.length === 0 && !isInputValid) {
      setErrorMessage('');
    } else if (!isInputValid) {
      setErrorMessage(`Please enter at least ${SEARCH_TERM_MIN} characters`);
    }
  };
  const doSearchInResultScreen = async () => {
    try {
      const response = await searchAPI.searchForum(envState.base_url, debouncedValue, apiPage, rootProductCategory.name, authState.api_key);
      if (response && isArrayValid(response.posts)) {
        const {
          posts,
          topics
        } = response;
        if (posts.length < SEARCH_RESULTS_MAX) {
          setApiHasMore(false);
        } else {
          setApiPage(currentPage => currentPage + 1);
        }
        const updatedPosts = combinePostsAndTopics(posts, topics);
        setSearchResults(updatedPosts);
        setSearchResultsLength(updatedPosts.length);
        return updatedPosts;
      }
    } catch (err) {
      console.log('Error while searching forum');
      return [];
    }
  };
  const loadMoreResults = async () => {
    if (apiHasMore) {
      try {
        const response = await searchAPI.searchForum(envState.base_url, debouncedValue, apiPage, rootProductCategory.name, authState.api_key);
        if (response && response.posts && response.topics) {
          const {
            posts,
            topics
          } = response;
          if (posts.length < SEARCH_RESULTS_MAX) {
            setApiHasMore(false);
          }
          if (isArrayValid(posts) && isArrayValid(topics)) {
            setSearchResultsLength(currentLength => currentLength + posts.length);
            const nextBatch = combinePostsAndTopics(posts, topics);
            setSearchResults(currentState => [...currentState, ...nextBatch]);
            return nextBatch;
          }
          return [];
        }
      } catch (err) {
        console.log('Error while loading more results');
        return [];
      }
    }
    console.log('Nothing more to load');
    return [];
  };
  return /*#__PURE__*/React.createElement(Box, {
    height: DEVICE_HEIGHT * 0.98
  }, /*#__PURE__*/React.createElement(Box, {
    h: DEVICE_HEIGHT * 0.2,
    bg: colors.searchResult.bg,
    mb: "2"
  }, /*#__PURE__*/React.createElement(Box, {
    h: "70%"
  }, /*#__PURE__*/React.createElement(ImageBackground, {
    source: carrotsImage,
    imageStyle: {
      opacity: 0.5
    },
    style: {
      height: '100%'
    }
  }, /*#__PURE__*/React.createElement(GoBackButton, {
    navigation: navigation
  }), /*#__PURE__*/React.createElement(Text, {
    fontSize: '2xl',
    color: colors.searchResult.header,
    fontWeight: "bold",
    pl: "2",
    pb: "2",
    style: {
      marginTop: 'auto'
    }
  }, "Search Results"))), /*#__PURE__*/React.createElement(Box, {
    alignItems: "center",
    justifyContent: "center",
    h: "30%",
    w: "90%",
    alignSelf: "center"
  }, /*#__PURE__*/React.createElement(HStack, {
    space: 1,
    alignItems: "center",
    justifyContent: "center",
    borderWidth: "1",
    borderColor: isInputValid ? colors.searchResult.input.border : colors.searchResult.input.invalid,
    borderRadius: 10,
    px: "2",
    py: "1",
    w: "100%"
  }, /*#__PURE__*/React.createElement(FontAwesomeIcon, {
    icon: faMagnifyingGlass
  }), /*#__PURE__*/React.createElement(Input, {
    w: "90%",
    borderWidth: "0",
    borderRadius: 15,
    value: searchTerm,
    placeholder: LANGUAGES.PLACEHOLDERS.SEARCH_FORUM,
    onChangeText: text => setSearchTerm(text),
    _hover: {
      backgroundColor: 'transparent'
    },
    _focus: {
      backgroundColor: 'transparent'
    }
  }), /*#__PURE__*/React.createElement(Select, {
    defaultValue: "",
    placeholder: LANGUAGES.PLACEHOLDERS.SEARCH_FORUM_CATEGORY,
    selectedValue: category,
    onValueChange: itemValue => setCategory(itemValue),
    borderWidth: 0,
    borderRadius: 10,
    w: "25%",
    bg: colors.searchResult.select.bg,
    color: colors.searchResult.select.text
  }, categories && categories.length && categories.map(category => /*#__PURE__*/React.createElement(Select.Item, {
    key: `${category.id}: ${category.description}`,
    label: category.name,
    value: category.name
  })))))), /*#__PURE__*/React.createElement(Box, {
    h: DEVICE_HEIGHT * 0.8,
    mt: "2"
  }, searchResultsLength ? /*#__PURE__*/React.createElement(Text, {
    fontSize: 'lg',
    fontWeight: "semibold",
    pl: "4"
  }, searchResultsLength >= SEARCH_RESULTS_MAX ? `${SEARCH_RESULTS_MAX}+` : searchResultsLength, ' ', searchResultsLength === 1 ? 'Result' : 'Results', category && ' in #' + category) : /*#__PURE__*/React.createElement(Text, {
    fontSize: 'lg',
    fontWeight: "semibold",
    pl: "4"
  }, "No posts found"), /*#__PURE__*/React.createElement(FlatList, {
    keyExtractor: item => item.id.toString() + item.created_at,
    data: searchResults,
    renderItem: renderItem,
    onEndReached: loadMoreResults,
    onEndReachedThreshold: 0.2
  })));
};
export default SearchForumScreen;
//# sourceMappingURL=SearchForumScreen.js.map